import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getAllExperienceTypesV2 } from '../../data/experienceTypesV2';

const GOAL_TYPES = [
  { value: 'streak', label: 'Streak', description: 'Complete experiences consecutively' },
  { value: 'count', label: 'Count', description: 'Complete a certain number of experiences' },
  { value: 'first-time', label: 'First Time', description: 'Complete an experience for the first time' },
  { value: 'custom', label: 'Custom', description: 'Create your own goal' }
];

export default function CreateGoalModal({ familyId, onClose }) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('count');
  const [experienceType, setExperienceType] = useState('');
  const [targetCount, setTargetCount] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  const experienceTypes = getAllExperienceTypesV2();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    if (type === 'count' && targetCount < 1) {
      toast.error('Target count must be at least 1');
      return;
    }

    setIsSaving(true);
    try {
      await addDoc(collection(db, 'families', familyId, 'goals'), {
        title: title.trim(),
        description: description.trim(),
        type,
        experienceType: experienceType || null,
        targetCount: type === 'count' ? targetCount : 1,
        currentCount: 0,
        completed: false,
        completedDate: null,
        proposedBy: user?.uid,
        approvedBy: [user?.uid], // Auto-approve for now
        createdAt: serverTimestamp()
      });

      toast.success('Goal created!');
      onClose();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-bold text-text-primary">Create New Goal</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-text-secondary" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Goal Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., 52 Shabbat Dinners"
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your goal..."
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none min-h-[100px]"
            />
          </div>

          {/* Goal Type */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Goal Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {GOAL_TYPES.map(goalType => (
                <button
                  key={goalType.value}
                  type="button"
                  onClick={() => setType(goalType.value)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    type === goalType.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary'
                  }`}
                >
                  <div className="font-semibold text-text-primary mb-1">
                    {goalType.label}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {goalType.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Experience Type */}
          {(type === 'count' || type === 'streak' || type === 'first-time') && (
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Experience Type (Optional)
              </label>
              <select
                value={experienceType}
                onChange={(e) => setExperienceType(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Any Experience</option>
                {experienceTypes.map(expType => (
                  <option key={expType.id} value={expType.id}>
                    {expType.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Target Count */}
          {type === 'count' && (
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Target Count *
              </label>
              <input
                type="number"
                value={targetCount}
                onChange={(e) => setTargetCount(parseInt(e.target.value) || 1)}
                min="1"
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white border-2 border-border rounded-lg font-semibold hover:border-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Creating...' : 'Create Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
